import { useEffect, useMemo, useState } from 'react';
import {
  Sun,
  Cloud,
  CloudRain,
  CloudSnow,
  CloudFog,
  CloudLightning,
  Moon,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  TrendingUp,
  Newspaper,
} from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import { useApi } from '../../hooks/useApi';
import { useLocation } from '../useLocation';
import { flavourLineFor, greetingFor, localeFromCountry } from '../greetings';
import { quoteOfTheDay, type Quote } from '../quotes';
import type { FeedsResponse } from '../feedTypes';

// Tick the clock once a minute (no need for second-level resolution on Home).
const useNow = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
};

const fmtDate = (d: Date) =>
  d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });

const fmtTime = (d: Date) =>
  d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

// Pick a weather icon from the Open-Meteo code. Fallback to the hour-of-day
// guess for when the feed is unavailable.
const iconForCode = (code: number | null, isDay: boolean, hour: number) => {
  if (code === null) {
    if (hour < 6 || hour >= 19) return Moon;
    if (hour >= 12 && hour < 16) return Sun;
    return Cloud;
  }
  if (code === 0 || code === 1) return isDay ? Sun : Moon;
  if (code === 2 || code === 3) return Cloud;
  if (code === 45 || code === 48) return CloudFog;
  if (code >= 51 && code <= 67) return CloudRain;
  if (code >= 71 && code <= 77) return CloudSnow;
  if (code >= 80 && code <= 82) return CloudRain;
  if (code >= 85 && code <= 86) return CloudSnow;
  if (code >= 95) return CloudLightning;
  return Cloud;
};

const useDailyQuote = (): Quote => {
  // Quote rotates once per day via quoteOfTheDay. Multiple-quote browsing
  // is handled by the ticker's panel navigation now, not a per-quote nudge.
  return quoteOfTheDay();
};

// Ticker panel kind. Sequence is computed dynamically based on which feeds
// are available — quote is always present, news/fx slot in if data exists.
type Tick = 'quote' | 'news' | 'fx';

// Returns position + manual + auto-advance with pause support.
// Pause is set true while the user hovers / focuses the ticker so they can
// read; auto-advance resumes when they leave. Manual nav (dots / arrows)
// also resets the auto-advance timer so the user gets a fresh 8s on the
// panel they just selected, not a 200ms flash before it jumps again.
const useTicker = (panels: Tick[], intervalMs: number, paused: boolean) => {
  const [idx, setIdx] = useState(0);
  const safe = panels.length > 0 ? idx % panels.length : 0;

  // Auto-advance — reset on every dependency change (idx, paused, panels).
  // That means a manual jump gives the user a fresh window before the next tick.
  useEffect(() => {
    if (paused || panels.length <= 1) return;
    const id = setTimeout(() => setIdx((n) => (n + 1) % panels.length), intervalMs);
    return () => clearTimeout(id);
  }, [idx, paused, panels.length, intervalMs]);

  const next = () => setIdx((n) => (panels.length ? (n + 1) % panels.length : 0));
  const prev = () =>
    setIdx((n) => (panels.length ? (n - 1 + panels.length) % panels.length : 0));
  const goTo = (i: number) => setIdx(i % Math.max(1, panels.length));

  return { idx: safe, panel: panels[safe] ?? 'quote', next, prev, goTo };
};

const fmtRate = (n: number): string => {
  if (n >= 100) return n.toFixed(0);
  if (n >= 1) return n.toFixed(3);
  return n.toFixed(4);
};

export const HeroBand = () => {
  const { user } = useAuth();
  const loc = useLocation();
  const now = useNow();
  const quote = useDailyQuote();

  // Live feeds. Refetches when /api/me reports a different country.
  const feeds = useApi<FeedsResponse>(`/api/feeds?country=${loc.country}`);
  const weather = feeds.data?.weather ?? null;
  const fx = feeds.data?.fx ?? null;
  const news = feeds.data?.news ?? null;
  const topHeadline = news?.items[0] ?? null;

  const firstName = (user?.displayName ?? '').split(' ')[0] || 'there';
  const locale = localeFromCountry(loc.country);
  const greeting = greetingFor(firstName, now.getHours(), locale);
  const flavour = flavourLineFor(locale, now);

  const WeatherIcon = iconForCode(
    weather?.code ?? null,
    weather?.isDay ?? (now.getHours() >= 6 && now.getHours() < 19),
    now.getHours(),
  );
  const cityLabel = weather?.city ?? loc.city;
  const tempLabel =
    weather?.tempC !== null && weather?.tempC !== undefined
      ? `${Math.round(weather.tempC)}°`
      : null;

  const panels = useMemo<Tick[]>(() => {
    const p: Tick[] = ['quote'];
    if (topHeadline) p.push('news');
    if (fx && Object.keys(fx.rates).length > 0) p.push('fx');
    return p;
  }, [topHeadline, fx]);

  // Two distinct pause sources:
  //  - `hovered` auto-pauses while the user is reading (mouseenter / focus)
  //  - `manuallyPaused` is set explicitly by the pause button and persists
  //    past mouseleave so the user can read at their own pace, walk away,
  //    come back, and pick up where they left off.
  // The button visibility follows the same rule as the prev/next chevrons
  // (hover/focus only) UNLESS manuallyPaused is true — then it stays
  // visible so the user has an obvious way to resume.
  const [hovered, setHovered] = useState(false);
  const [manuallyPaused, setManuallyPaused] = useState(false);
  const paused = hovered || manuallyPaused;
  const { idx: tickIdx, panel: tick, next, prev, goTo } = useTicker(panels, 8000, paused);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      next();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prev();
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      setManuallyPaused((p) => !p);
    }
  };

  return (
    <section
      aria-label="At a glance"
      className="hero-gradient relative overflow-hidden rounded-2xl border border-brand-navy/15 bg-gradient-to-br from-brand-navy via-brand-navy to-brand-orange/80 p-5 text-white shadow-sm sm:p-6"
    >
      <div
        aria-hidden="true"
        className="hero-blob pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-brand-orange/30 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="hero-blob-slow pointer-events-none absolute -left-20 -bottom-24 h-56 w-56 rounded-full bg-brand-navy/40 blur-3xl"
      />

      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{greeting}!</h1>
          <p className="mt-1.5 max-w-prose text-sm text-white/85">
            {flavour.line}
            {flavour.gloss && (
              <span className="ml-2 text-xs text-white/55">({flavour.gloss})</span>
            )}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3 text-sm text-white/85">
          <div className="flex items-center gap-2">
            <WeatherIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {tempLabel && (
              <span className="font-semibold tabular-nums text-white">{tempLabel}</span>
            )}
          </div>
          <div className="text-right leading-tight">
            <div className="font-semibold tabular-nums text-white">{fmtTime(now)}</div>
            <div className="text-[11px] text-white/65">
              {fmtDate(now)} · {cityLabel}
            </div>
          </div>
        </div>
      </div>

      {/* Rotating ticker — quote / news / FX. Pause on hover/focus so the
          user can read; ←/→ to navigate, space to toggle pause. */}
      <div
        role="region"
        aria-label="Live updates"
        aria-roledescription="carousel"
        tabIndex={0}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onKeyDown={onKey}
        className="group relative mt-4 rounded-lg border border-white/15 bg-white/10 backdrop-blur-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      >
        <div className="flex items-start gap-3 px-3 py-2.5">
          <TickerIcon tick={tick} />
          <div
            key={`${tick}-${tickIdx}`}
            className="ticker-enter min-w-0 flex-1 text-sm"
            aria-live="polite"
            aria-atomic="true"
          >
            {tick === 'quote' && (
              <>
                <div className="italic text-white/95">"{quote.text}"</div>
                {(quote.attribution || quote.gloss) && (
                  <div className="mt-0.5 text-[11px] text-white/65">
                    {quote.attribution && <span>— {quote.attribution}</span>}
                    {quote.gloss && (
                      <span className={quote.attribution ? ' ml-2' : ''}>({quote.gloss})</span>
                    )}
                  </div>
                )}
              </>
            )}
            {tick === 'news' && topHeadline && (
              <a
                href={topHeadline.url}
                target="_blank"
                rel="noreferrer"
                className="block min-w-0 hover:underline"
              >
                <div className="line-clamp-2 text-white/95">{topHeadline.title}</div>
                <div className="mt-0.5 text-[11px] text-white/65">{topHeadline.source}</div>
              </a>
            )}
            {tick === 'fx' && fx && <FxLine fx={fx} />}
          </div>
        </div>

        {/* Footer — three-column layout:
             - left spacer mirrors the right cluster so the dots stay
               centered regardless of how many controls show
             - dots in the middle
             - prev / pause / next on the right; hidden by default and
               only visible on hover/focus, except the pause button stays
               visible whenever the carousel is manually paused so the
               user has an obvious way to resume.
         */}
        {panels.length > 1 && (
          <div className="grid grid-cols-3 items-center gap-2 border-t border-white/10 px-3 py-1.5 text-[10px] text-white/60">
            {/* Invisible spacer to balance the right cluster. */}
            <div aria-hidden="true" />

            <div className="flex items-center justify-center gap-1.5">
              {panels.map((p, i) => (
                <button
                  key={p}
                  type="button"
                  aria-label={`Go to ${p}`}
                  aria-current={tickIdx === i ? 'true' : undefined}
                  onClick={() => goTo(i)}
                  className={[
                    'h-1.5 rounded-full transition-all',
                    tickIdx === i ? 'w-5 bg-white/90' : 'w-1.5 bg-white/40 hover:bg-white/70',
                  ].join(' ')}
                />
              ))}
            </div>

            <div
              className={[
                'flex items-center justify-end gap-0.5 transition-opacity',
                // Hover/focus reveals all controls. Manually-paused state
                // keeps them visible too so the user can un-pause.
                manuallyPaused
                  ? 'opacity-100'
                  : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
              ].join(' ')}
            >
              <button
                type="button"
                onClick={prev}
                aria-label="Previous"
                className="grid h-6 w-6 place-items-center rounded text-white/70 hover:bg-white/15 hover:text-white"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setManuallyPaused((p) => !p)}
                aria-label={manuallyPaused ? 'Resume' : 'Pause'}
                aria-pressed={manuallyPaused}
                className={[
                  'grid h-6 w-6 place-items-center rounded transition-colors',
                  manuallyPaused
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:bg-white/15 hover:text-white',
                ].join(' ')}
              >
                {manuallyPaused ? (
                  <Play className="h-3.5 w-3.5" />
                ) : (
                  <Pause className="h-3.5 w-3.5" />
                )}
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next"
                className="grid h-6 w-6 place-items-center rounded text-white/70 hover:bg-white/15 hover:text-white"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const TickerIcon = ({ tick }: { tick: Tick }) => {
  if (tick === 'news') return <Newspaper className="mt-0.5 h-4 w-4 shrink-0 text-white/85" aria-hidden="true" />;
  if (tick === 'fx') return <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-white/85" aria-hidden="true" />;
  return <span className="mt-0.5 text-base leading-none">💭</span>;
};

const FxLine = ({ fx }: { fx: NonNullable<FeedsResponse['fx']> }) => {
  const pairs = Object.entries(fx.rates);
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-3 text-white/95">
        {pairs.map(([k, v]) => (
          <span key={k} className="flex items-baseline gap-1">
            <span className="text-[11px] text-white/65">1 {fx.base} =</span>
            <span className="font-semibold tabular-nums">{fmtRate(v)}</span>
            <span className="text-[11px] text-white/65">{k}</span>
          </span>
        ))}
      </div>
      <div className="mt-0.5 text-[11px] text-white/55">
        Live FX · refreshes every 15 min
      </div>
    </>
  );
};

// Lower-key variant for Focused users — same greeting, slimmer.
export const HeroBandCompact = () => {
  const { user } = useAuth();
  const loc = useLocation();
  const now = useNow();
  const firstName = (user?.displayName ?? '').split(' ')[0] || 'there';
  const locale = localeFromCountry(loc.country);
  const greeting = greetingFor(firstName, now.getHours(), locale);

  return (
    <header className="flex flex-wrap items-baseline justify-between gap-2">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight">{greeting}</h1>
        <p className="mt-0.5 text-sm text-text-secondary">
          {fmtDate(now)} · {loc.city}
        </p>
      </div>
      <div className="text-xs tabular-nums text-text-tertiary">{fmtTime(now)}</div>
    </header>
  );
};
